import { useRef, useEffect, useState, useMemo } from "react";
import Pagination from "../../../../Components/Pagination";
import Button from "../../../../Core/Button/Button";
import GetIcon from "../../../../utils/Icon";
import SearchableDropdown from "../../../../Core/Fields/Floating/SearchableDropdown";
import {
  useGetLiabilityAccountsByCompanyQuery,
  useGetAllcompanyQuery,
} from "../../../../store/api/supplier";

const Table = ({ accounts }) => {
  const { value: rowsRaw = [], setValue = () => {} } = accounts || {};
  const rows = Array.isArray(rowsRaw) ? [...rowsRaw] : [];

  const [companyList, setCompanyList] = useState([]);
  const [accountsCache, setAccountsCache] = useState({});
  const [selectedCompanyForApi, setSelectedCompanyForApi] = useState(null);
  const [pendingRowIndex, setPendingRowIndex] = useState(null);

  const tableRef = useRef();
  const dropdownRefCompany = useRef(null);

  const [rowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [focusedCell, setFocusedCell] = useState({ rowIndex: null, field: null });
  const [showDropdown, setShowDropdown] = useState({ rowIndex: null, field: null });

  const [offsetCompany, setOffsetCompany] = useState(0);
  const [searchCompany, setSearchCompany] = useState("");
  const [hasMoreCompany, setHasMoreCompany] = useState(true);

  const { data: companyData } = useGetAllcompanyQuery();
  const { data: accountsData } = useGetLiabilityAccountsByCompanyQuery(selectedCompanyForApi, {
    skip: !selectedCompanyForApi,
  });

  // ✅ Load all companies once
  useEffect(() => {
    if (companyData?.data) {
      setCompanyList(companyData.data);
    }
  }, [companyData]);

  useEffect(() => {
    if (accountsData && selectedCompanyForApi && pendingRowIndex !== null) {
      // Store accounts in cache
      setAccountsCache((prev) => ({
        ...prev,
        [selectedCompanyForApi]: accountsData.data,
      }));

      // ✅ Update row safely (no mutation)
      const updatedRows = rows.map((row, index) => {
        if (index === pendingRowIndex) {
          return {
            ...row,
            account: accountsData.data?.[0]?.name || "",
          };
        }
        return row;
      });

      setValue(updatedRows);
      setPendingRowIndex(null);
      setSelectedCompanyForApi(null);
    }
  }, [accountsData]);

  // ✅ Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = rows.slice(indexOfFirstRow, indexOfLastRow);
  const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;

  const createNewRow = () => ({ company: "", account: "" });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(rows.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleRowSelection = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

const updateRow = async (index, field, value) => {
  // Step 1: Deep clone safely to avoid frozen object errors
  const clonedRows = JSON.parse(JSON.stringify(rows || []));
  const currentRow = clonedRows[index] || {};

  // Step 2: Update the field value
  currentRow[field] = value;
  clonedRows[index] = currentRow;

  // Step 3: Update UI immediately
  setValue(clonedRows);

  // Step 4: Handle company selection → trigger account auto-fill
  if (field === "company" && value) {
    try {
      // If cached, use cache instantly
      if (accountsCache[value]) {
        const cachedAccounts = accountsCache[value];
        clonedRows[index].account = cachedAccounts[0]?.name || "";
        setValue(clonedRows);
        return;
      }

      // Otherwise, trigger API call
      setSelectedCompanyForApi(value);
      setPendingRowIndex(index);
    } catch (err) {
      console.error("Error updating company or fetching accounts:", err);
    }
  }
};

  const handleAddRow = () => {
    const updated = [...rows, createNewRow()];
    setValue(updated);
    const lastPage = Math.ceil(updated.length / rowsPerPage);
    if (currentPage !== lastPage) setCurrentPage(lastPage);
  };

  const handleDeleteSelectedRows = () => {
    const updatedRows = rows.filter((_, idx) => !selectedRows.includes(idx));
    setValue(updatedRows);
    setSelectedRows([]);
    const lastPage = Math.ceil(updatedRows.length / rowsPerPage);
    if (currentPage > lastPage) setCurrentPage(lastPage);
  };

  // ✅ Handle company selection
  const handleCompanySelect = (rowIndex, companyName) => {
    updateRow(rowIndex, "company", companyName);

    if (!companyName) {
      updateRow(rowIndex, "account", "");
      return;
    }

    // use cached accounts if available
    if (accountsCache[companyName]) {
      updateRow(rowIndex, "account", accountsCache[companyName][0]?.name || "");
      return;
    }

    // otherwise fetch
    updateRow(rowIndex, "account", "Loading...");
    setSelectedCompanyForApi(companyName);
    setPendingRowIndex(rowIndex);
    console.log('sdfsdf',companyName)
  };

  // ✅ Dropdown focus/blur handling
  const handleDropdownFocus = (rowIndex, field) => {
    setShowDropdown({ rowIndex, field });
    setFocusedCell({ rowIndex, field });
  };

  const handleDropdownBlur = () => {
    setTimeout(() => setShowDropdown({ rowIndex: null, field: null }), 150);
  };

  // ✅ Filter company list for search
  const filteredCompanies = useMemo(() => {
    return companyList.filter((c) =>
      c.name.toLowerCase().includes(searchCompany.toLowerCase())
    );
  }, [companyList, searchCompany]);

  // ✅ Column resizing (optional)
  useEffect(() => {
    const cols = tableRef.current?.querySelectorAll("th") || [];
    cols.forEach((col) => {
      if (col.querySelector(".resizer")) return;
      const resizer = document.createElement("div");
      resizer.className = "resizer";
      col.appendChild(resizer);

      let x = 0;
      let w = 0;

      const onMouseDown = (e) => {
        x = e.clientX;
        w = col.offsetWidth;
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      };

      const onMouseMove = (e) => {
        const dx = e.clientX - x;
        col.style.width = `${w + dx}px`;
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      resizer.addEventListener("mousedown", onMouseDown);
    });
  }, []);

  return (
    <div className="table-container">
      <table className="table table-bordered mb-1" ref={tableRef}>
        <thead className="tableHead1">
          <tr>
            <th style={{ width: "4%", textAlign: "center" }}>
              <input
                type="checkbox"
                className="form-check-input"
                style={{
                  backgroundColor: isAllSelected ? "#270b79" : "",
                  borderColor: isAllSelected ? "#270b79" : "",
                }}
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
            </th>
            <th style={{ width: "4%", textAlign: "center" }}>S.No</th>
            <th style={{ width: "350px" }}>Company</th>
            <th style={{ width: "350px" }}>Default Account</th>
            <th style={{ width: "250px", textAlign: "center" }}>
              <GetIcon iconName="PiSlidersHorizontalBold" size={12} />
            </th>
          </tr>
        </thead>

        <tbody>
          {currentRows.map((row, index) => {
            const globalIndex = indexOfFirstRow + index;
            return (
              <tr key={globalIndex} style={{ height: "25px" }}>
                <td className="p-0" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    style={{
                      backgroundColor: selectedRows.includes(globalIndex)
                        ? "#270b79"
                        : "",
                      borderColor: selectedRows.includes(globalIndex)
                        ? "#270b79"
                        : "",
                      marginTop: "8px",
                    }}
                    checked={selectedRows.includes(globalIndex)}
                    onChange={() => toggleRowSelection(globalIndex)}
                  />
                </td>

                <td style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "0.8rem" }}>{globalIndex + 1}</span>
                </td>

                {/* ✅ Company Dropdown */}
                <SearchableDropdown
                  globalIndex={globalIndex}
                  field="company"
                  value={row.company || ""}
                   onChange={(val) => updateRow(globalIndex, "company", val)}
                //   onChange={(val) => handleCompanySelect(globalIndex, val)}
                  onSearch={(text) => setSearchCompany(text)}
                  dropdownData={filteredCompanies}
                  placeholder="Select Company"
                  focusedCell={focusedCell}
                  setFocusedCell={setFocusedCell}
                  showDropdown={showDropdown}
                  setShowDropdown={setShowDropdown}
                  dropdownRef={dropdownRefCompany}
                  offset={offsetCompany}
                  setOffset={setOffsetCompany}
                  search_input={searchCompany}
                  hasMore={hasMoreCompany}
                  getData={() => {}}
                  onFocus={() => handleDropdownFocus(globalIndex, "company")}
                  onBlur={handleDropdownBlur}
                />

                {/* ✅ Default Account */}
                <td className="p-0">
                  <input
                    type="text"
                    placeholder="Default Account"
                    style={{ textAlign: "center", fontSize: "0.8rem" }}
                    className="form-control tableinput noborder"
                    value={row.account || ""}
                    readOnly
                  />
                </td>

                <td
                  style={{
                    position: "relative",
                    width: "40px",
                  }}
                >
                  <img
                    src="/Maskgroup.png"
                    alt="edit"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "12px",
                      height: "12px",
                      pointerEvents: "none",
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ✅ Footer */}
      <div className="d-flex pb-2 justify-content-between align-items-center">
        <div className="d-flex" style={{ gap: "4px" }}>
          {selectedRows.length > 0 && (
            <Button
              onClick={handleDeleteSelectedRows}
              className="customdeletebtn"
              iconName="MdDeleteOutline"
            >
              Delete
            </Button>
          )}
          <Button
            onClick={handleAddRow}
            className="Addrowdesign"
            iconName="FiPlus"
            iconSize={12}
            iconWrapperStyle={{
              backgroundColor: "#F6D593",
              padding: "1px",
              display: "flex",
            }}
          >
            Add Row
          </Button>
          <Button
            className="createbtn d-flex"
            iconName="MdOutlineFileDownload"
            style={{ height: "28px" }}
          >
            Download
          </Button>
        </div>

        {rows.length > 10 && (
          <>
            <div className="d-flex align-items-center centerpagination">
              <Pagination
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                pageSize={rowsPerPage}
                totalrecords={rows.length}
              />
            </div>
            <div className="d-flex">
              <span className="px-2" style={{ color: "#270B79" }}>
                Showing {indexOfFirstRow + 1} to{" "}
                {Math.min(indexOfLastRow, rows.length)} of {rows.length} entries
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Table;
